import { io, Socket } from 'socket.io-client';
import {
  ClientEvent,
  ServerEvent,
  AuthSuccessPayload,
  AuthErrorPayload,
  MatchFoundPayload,
  ErrorPayload,
  DiceSyncPayload,
  DicePhase,
  DiceThrowParams,
  FullGameState,
  GameUpdate,
  GameChatMessage,
  BetZone,
  DiceGearUpdatedPayload,
  OfferWire,
  OfferClaimResult,
  ChatMessagePayload,
  PlayerSearchResultsPayload,
  FriendFavouritePayload,
  FriendGiftSentPayload,
  GiftCollectedPayload,
  FriendOnlinePayload,
  FriendOfflinePayload,
  FriendAddedPayload,
  FriendListPayload,
  FriendPendingListPayload,
  FriendRemovedPayload,
  FriendBlockPayload,
  BlockedListPayload,
  FriendUnblockedPayload,
  HomeDataPayload,
  DailyBonusResultPayload,
} from '@sdl/shared';
import type {
  Tutorial2TaskId,
  Tutorial2UpdatedPayload,
  DailyChallengeId,
  DailyChallengesUpdatePayload,
} from '@sdl/shared';
import type {
  VoiceWelcomePayload,
  VoicePeerJoinedPayload,
  VoicePeerLeftPayload,
  VoiceSdpRelayPayload,
  VoiceIceRelayPayload,
  VoiceEndRelayPayload,
} from '@sdl/shared';
import type {
  ChatMessageType,
  NotificationListPayload,
  GameNotification,
  LobbyStatePayload,
  LobbyPlayerJoinedPayload,
  LobbyPlayerLeftPayload,
  LobbyPlayerUpdatedPayload,
  LobbyChatMessagePayload,
  LobbyRoomUpsertPayload,
  LobbyRoomRemovedPayload,
  LobbyCreateRoomPayload,
  LobbyJoinRoomPayload,
  UsernameChangeResultPayload,
} from '@sdl/shared';
import { EventEmitter } from '../engine/core/EventEmitter';
import { serverConfig } from './ServerConfig';

// Shooting config from server
export interface ShootingConfigPayload {
  config: {
    holdHeight: number;
    energyGain: number;
    maxPower: number;
    baseForward: number;
    lobStrength: number;
    lobDuration: number;
    diceMass: number;
  };
  version: number;
}

interface NetworkEvents {
  'main:connected': void;
  'main:disconnected': void;
  'game:connected': void;
  'game:disconnected': void;
  /** The initial connection to the allocated game server never succeeded
   *  (server down/unreachable within the deadline). Fires at most once per
   *  join attempt; not emitted for in-game reconnect blips. */
  'game:connect_failed': void;
  'auth:success': AuthSuccessPayload;
  'session:replaced': { reason?: string };
  'player:banned': { banned: boolean };
  'auth:error': AuthErrorPayload;
  'match:found': MatchFoundPayload;
  'match:error': ErrorPayload;
  'game:state': GameStateSnapshot;
  'game:player_joined': { playerId: string; username: string; equippedDice?: string[] };
  'game:turn_start': GameTurnStartPayload;
  'game:roll_result': GameRollResultPayload;
  'game:bet_placed': GameBetPlacedPayload;
  /** Targeted ack to the zone creator. Carries `requestId` so the
   *  client can match its parked bills to the new zone and animate
   *  them in. Spectator zone visibility comes from `game:update.betZones`. */
  'game:bet_zone_create_ack': { requestId: string; zoneId: string };
  'game:bet_zone_fade_result': { requestId: string; zoneId: string; acceptedAmount: number; rejectedAmount: number };
  'game:bet_zone_create_rejected': { requestId: string; reason: string; maxAmount?: number };
  'game:dice_sync': DiceSyncPayload;
  'dice:shake': { playerId: string; intensity?: number };
  // In-match public chat — one line broadcast to the room
  'game:chat_message': GameChatMessage;
  // Server-authoritative events
  'state:full': FullGameState;
  'dice:phase': {
    playerId: string;
    phase: DicePhase;
    diceCount?: number;
    throwParams?: DiceThrowParams;
    finalStates?: Array<{
      position: { x: number; y: number; z: number };
      quaternion: { x: number; y: number; z: number; w: number };
    }>;
  };
  'dice:result': { playerId: string; results: number[] };
  'reconnect:token': { token: string };
  'reconnect:ok': { playerId: string };
  'reconnect:fail': { code: string; message: string };
  /** Server refused to seat us (full / already seated / broke / join failed). */
  'game:join_rejected': { code: string; message: string };
  // Dice gear
  'player:gear_updated': DiceGearUpdatedPayload;
  // Tutorial part 2 task board
  'tutorial2:updated': Tutorial2UpdatedPayload;
  'daily_challenges:update': DailyChallengesUpdatePayload;
  // Config sync
  'config:shooting': ShootingConfigPayload;
  // Support events
  'support:ticket_reply': { ticketId: string };
  // PFP moderation: admin removed this player's profile picture
  'pfp:removed': Record<string, never>;
  // Social events
  'chat:message': ChatMessagePayload;
  'friend:online': FriendOnlinePayload;
  'friend:offline': FriendOfflinePayload;
  'friend:added': FriendAddedPayload;
  'friend:list': FriendListPayload;
  'friend:pending_list': FriendPendingListPayload;
  'friend:removed': FriendRemovedPayload;
  'friend:favourited': FriendFavouritePayload;
  'friend_gift:sent': FriendGiftSentPayload;
  'gift:collected': GiftCollectedPayload;
  'friend:blocked': FriendBlockPayload;
  'friend:unblocked': FriendUnblockedPayload;
  'blocked:list': BlockedListPayload;
  'player:search_results': PlayerSearchResultsPayload;
  // Notifications
  'notification:list': NotificationListPayload;
  'notification:new': GameNotification;
  'notification:removed': { notificationId: string };
  'notification:unseen_count': { count: number };
  // Home screen data
  'home:data': HomeDataPayload;
  // Daily bonus
  'daily_bonus:result': DailyBonusResultPayload;
  // Balance
  'balance:update': { stacks: number; gold: number; diceTokens?: number };
  // Offers (economy spec §8)
  'offers:active': { offers: OfferWire[] };
  'offers:update': Record<string, never>;
  'offer:claim_result': OfferClaimResult;
  // Connect-account 100M bonus result
  'account:connect_bonus_result': { success: boolean; amount: number; newBalance: number; error?: string };
  // Social sign-in: new identity → prompt for a name
  'auth:social_needs_username': { defaultName: string; takenName?: string };
  // Live username-availability answer (name-prompt typing)
  'username:check_result': { requestId: number; username: string; available: boolean };
  // Outcome of a Settings username change (success / reason)
  'username:change_result': UsernameChangeResultPayload;
  // Payouts
  'game:payout': { amount: number; newBalance: number; reason?: string };
  // Roll wins — array of all wins from a roll settlement
  'game:roll_wins': { wins: Array<{ playerId: string; amount: number; betType: string }> };
  // Craps: one-roll sidebet resolved against the just-completed dice result.
  // Drives per-zone payout flyouts on the come-out / point result beat.
  'craps:sidebet_resolved': {
    zoneId: string;
    ownerPlayerId: string;
    betType: string;
    outcome: 'win' | 'lose' | 'push';
    /** Map of playerId → total amount returned from this resolution. */
    payouts: Array<{ playerId: string; amount: number }>;
  };
  // Craps: end-of-turn / forfeit beat (rendered as "NO RESULT" overlay on
  // forfeit; the client may also use this to drive a settle-result flyout).
  'craps:turn_result': {
    kind: 'shooter_wins' | 'shooter_loses' | 'forfeit' | 'no_result';
    triggerPlayerId?: string | null;
    message?: string;
    finalOutcome?: unknown;
    pointRolls?: Array<[number, number]>;
    comeOutRoll?: [number, number];
  };
  // Voice chat (WebRTC signaling relays — same family as dice:shake; the
  // VoiceClient subscribes to these and drives the peer mesh).
  'voice:welcome': VoiceWelcomePayload;
  'voice:peer-joined': VoicePeerJoinedPayload;
  'voice:peer-left': VoicePeerLeftPayload;
  'voice:offer': VoiceSdpRelayPayload;
  'voice:answer': VoiceSdpRelayPayload;
  'voice:ice': VoiceIceRelayPayload;
  'voice:end': VoiceEndRelayPayload;
  // Unified game update (replaces most individual events)
  'game:update': GameUpdate;
  // Server removed us at a round boundary for not being actively present
  // (backgrounded). UI shows the inactivity modal + exits to the lobby.
  'game:kicked': { reason?: string };
  // Sidebet window (broadcast from server when a player requests sidebets during PLAYER_ROLL)
  'game:sidebet_window': { requestedBy: string };
  // Lobby (presence + public chat + room list)
  'lobby:state': LobbyStatePayload;
  'lobby:player_joined': LobbyPlayerJoinedPayload;
  'lobby:player_left': LobbyPlayerLeftPayload;
  'lobby:player_updated': LobbyPlayerUpdatedPayload;
  'lobby:chat_message': LobbyChatMessagePayload;
  'lobby:room_upsert': LobbyRoomUpsertPayload;
  'lobby:room_removed': LobbyRoomRemovedPayload;
}

export interface GamePlayerSnapshot {
  playerId: string;
  username?: string;
  avatar?: string;
  balance?: number;
  bet?: number;
  isReady?: boolean;
  equippedDice?: string[];
  result?: { dice: number[]; type: string; value: number } | null;
}

export interface GameStateSnapshot {
  roomId: string;
  gameType: 'ceelo' | 'craps' | 'solo';
  state: string;
  pot?: number;
  bankerId?: string | null;
  currentPlayerId?: string | null;
  roundNumber?: number;
  timeRemaining?: number;
  players?: GamePlayerSnapshot[];
  sideBets?: Array<{
    playerId: string;
    type: string;
    amount: number;
    targetPlayerId?: string;
    odds: number;
  }>;
  betZones?: BetZone[];
  bankerResult?: { dice: number[]; type: string; value: number; rank: number } | null;
  /** True once the banker has rolled this round — used to gate post-roll sidebet types (BW/BL) */
  bankerHasRolled?: boolean;
  /** Server-authoritative sidebet codes for the current window (mirrored from
   *  FullGameState.availableSidebets). Client maps codes → display labels. */
  availableSidebets?: string[];
  hiLoRounds?: Array<{ rolls: Record<string, number>; contenders: string[] }>;
  hiLoContenders?: string[];
  hiLoRolls?: Record<string, number>;
  shooterQueue?: string[];
  /** Game-specific state bag mirrored from FullGameState. Used by craps for
   *  point/comeOutRoll/sidebetPreArmed; unused by cee-lo. */
  customState?: Record<string, unknown>;
  currentShooterIndex?: number;
}

export interface GameTurnStartPayload {
  playerId: string;
  action?: string;
  timeout?: number;
}

export interface GameRollResultPayload {
  playerId: string;
  dice: number[];
  result?: unknown;
}

export interface GameBetPlacedPayload {
  playerId: string;
  amount: number;
}

export class NetworkManager extends EventEmitter<NetworkEvents> {
  private mainSocket: Socket | null = null;
  private gameSocket: Socket | null = null;
  /** Pending social sign-in payload, emitted on connect (set before connecting). */
  private pendingSocialAuth: { provider: 'google' | 'apple'; token: string; tokenType?: 'access' | 'id'; username?: string; guestToken?: string } | null = null;
  private playerId: string | null = null;
  private lastGameState: GameStateSnapshot | null = null;
  private pendingMatch: MatchFoundPayload | null = null;
  /**
   * Estimated offset between this device's clock and the game server's clock,
   * in ms (`serverNow ≈ Date.now() + timeOffset`). Updated by `time:sync`
   * round-trips on the game socket. Used by countdown rings to compensate for
   * phone clock skew so the tween lands within ~50ms of the server's
   * setTimeout firing. Zero until the first sync completes.
   */
  private timeOffset = 0;
  private timeSyncInterval: ReturnType<typeof setInterval> | null = null;
  /** Hard deadline for the INITIAL game-server connection. Cleared on first
   *  'connect'; if it fires first, the join is treated as failed. */
  private gameConnectDeadline: ReturnType<typeof setTimeout> | null = null;
  /** True once the game socket has connected at least once this session, so a
   *  later reconnect blip isn't mistaken for an initial-connect failure. */
  private gameEverConnected = false;

  /** Game-server time estimate. Falls back to local clock if no sync has run yet. */
  serverNow(): number {
    return Date.now() + this.timeOffset;
  }

  connectMain(): void {
    if (this.mainSocket) return;

    const socket = io(serverConfig.mainServerUrl, {
      transports: import.meta.env.DEV ? ['polling', 'websocket'] : ['websocket'],
      withCredentials: true,
    });

    this.mainSocket = socket;

    socket.on('connect', () => {
      this.emit('main:connected', undefined);
      // Social sign-in (set before connect) takes priority over guest auth.
      if (this.pendingSocialAuth) {
        this.mainSocket?.emit(ClientEvent.AUTH_SOCIAL, this.pendingSocialAuth);
      } else {
        this.loginGuest();
      }
    });

    socket.on('disconnect', () => {
      this.emit('main:disconnected', undefined);
    });

    socket.on(ServerEvent.AUTH_SUCCESS, (data: AuthSuccessPayload) => {
      this.playerId = data.playerId;
      this.emit('auth:success', data);
    });

    socket.on(ServerEvent.AUTH_ERROR, (data: AuthErrorPayload) => {
      this.emit('auth:error', data);
    });

    // Same account signed in on another device → this session is kicked. Stop
    // reconnecting (the server-initiated disconnect already won't auto-reconnect,
    // but belt-and-suspenders) and let the UI surface it.
    socket.on(ServerEvent.SESSION_REPLACED, (data: { reason?: string }) => {
      socket.io.opts.reconnection = false;
      this.emit('session:replaced', data ?? {});
    });

    // Live ban / unban pushed by an admin while connected.
    socket.on(ServerEvent.PLAYER_BANNED, (data: { banned: boolean }) => {
      this.emit('player:banned', data ?? { banned: true });
    });

    socket.on(ServerEvent.MATCH_FOUND, (data: MatchFoundPayload) => {
      // Store match data — don't connect yet. The client must finish scene
      // setup first so controllers exist when state:full arrives (UDG pattern).
      // Call connectToGameServer() after scene is ready.
      this.pendingMatch = data;
      this.emit('match:found', data);
    });

    socket.on(ServerEvent.ROOM_ERROR, (data: ErrorPayload) => {
      this.emit('match:error', data);
    });

    socket.on(ServerEvent.DICE_GEAR_UPDATED, (data: DiceGearUpdatedPayload) => {
      this.emit('player:gear_updated', data);
    });

    socket.on(ServerEvent.TUTORIAL2_UPDATED, (data: Tutorial2UpdatedPayload) => {
      this.emit('tutorial2:updated', data);
    });

    socket.on(ServerEvent.DAILY_CHALLENGES_UPDATE, (data: DailyChallengesUpdatePayload) => {
      this.emit('daily_challenges:update', data);
    });

    // Support: admin replied to a ticket (raw string event, not a ServerEvent
    // enum — it's a player unicast, reconstructable from listTickets on resume).
    socket.on('support:ticket_reply', (data: { ticketId?: string }) => {
      this.emit('support:ticket_reply', { ticketId: data?.ticketId ?? '' });
    });

    // PFP moderation: admin removed this player's profile picture. Resume-safe —
    // the blanked picture also rides state:full / profile fetches on reconnect.
    socket.on('pfp:removed', () => {
      this.emit('pfp:removed', {});
    });

    // Social events
    socket.on(ServerEvent.CHAT_MESSAGE, (data: ChatMessagePayload) => {
      this.emit('chat:message', data);
    });

    socket.on(ServerEvent.FRIEND_ONLINE, (data: FriendOnlinePayload) => {
      this.emit('friend:online', data);
    });

    socket.on(ServerEvent.FRIEND_OFFLINE, (data: FriendOfflinePayload) => {
      this.emit('friend:offline', data);
    });

    socket.on(ServerEvent.FRIEND_ADDED, (data: FriendAddedPayload) => {
      this.emit('friend:added', data);
    });

    socket.on(ServerEvent.FRIEND_LIST, (data: FriendListPayload) => {
      this.emit('friend:list', data);
    });

    socket.on(ServerEvent.FRIEND_PENDING_LIST, (data: FriendPendingListPayload) => {
      this.emit('friend:pending_list', data);
    });

    socket.on(ServerEvent.FRIEND_REMOVED, (data: FriendRemovedPayload) => {
      this.emit('friend:removed', data);
    });

    socket.on(ServerEvent.FRIEND_FAVOURITED, (data: FriendFavouritePayload) => {
      this.emit('friend:favourited', data);
    });

    socket.on(ServerEvent.FRIEND_GIFT_SENT, (data: FriendGiftSentPayload) => {
      this.emit('friend_gift:sent', data);
    });

    socket.on(ServerEvent.GIFT_COLLECTED, (data: GiftCollectedPayload) => {
      this.emit('gift:collected', data);
    });

    socket.on(ServerEvent.FRIEND_BLOCKED, (data: FriendBlockPayload) => {
      this.emit('friend:blocked', data);
    });

    socket.on(ServerEvent.FRIEND_UNBLOCKED, (data: FriendUnblockedPayload) => {
      this.emit('friend:unblocked', data);
    });

    socket.on(ServerEvent.BLOCKED_LIST, (data: BlockedListPayload) => {
      this.emit('blocked:list', data);
    });

    socket.on(ServerEvent.PLAYER_SEARCH_RESULTS, (data: PlayerSearchResultsPayload) => {
      this.emit('player:search_results', data);
    });

    // Lobby events — re-emitted as typed events for LobbyService to consume.
    socket.on(ServerEvent.LOBBY_STATE, (data: LobbyStatePayload) => {
      this.emit('lobby:state', data);
    });
    socket.on(ServerEvent.LOBBY_PLAYER_JOINED, (data: LobbyPlayerJoinedPayload) => {
      this.emit('lobby:player_joined', data);
    });
    socket.on(ServerEvent.LOBBY_PLAYER_LEFT, (data: LobbyPlayerLeftPayload) => {
      this.emit('lobby:player_left', data);
    });
    socket.on(ServerEvent.LOBBY_PLAYER_UPDATED, (data: LobbyPlayerUpdatedPayload) => {
      this.emit('lobby:player_updated', data);
    });
    socket.on(ServerEvent.LOBBY_CHAT_MESSAGE, (data: LobbyChatMessagePayload) => {
      this.emit('lobby:chat_message', data);
    });
    socket.on(ServerEvent.LOBBY_ROOM_UPSERT, (data: LobbyRoomUpsertPayload) => {
      this.emit('lobby:room_upsert', data);
    });
    socket.on(ServerEvent.LOBBY_ROOM_REMOVED, (data: LobbyRoomRemovedPayload) => {
      this.emit('lobby:room_removed', data);
    });

    // Notification events
    socket.on(ServerEvent.NOTIFICATION_LIST, (data: NotificationListPayload) => {
      this.emit('notification:list', data);
    });

    socket.on(ServerEvent.NOTIFICATION_NEW, (data: GameNotification) => {
      this.emit('notification:new', data);
    });

    socket.on(ServerEvent.NOTIFICATION_REMOVED, (data: { notificationId: string }) => {
      this.emit('notification:removed', data);
    });

    socket.on(ServerEvent.NOTIFICATION_UNSEEN_COUNT, (data: { count: number }) => {
      this.emit('notification:unseen_count', data);
    });

    // Home screen data
    socket.on(ServerEvent.HOME_DATA, (data: HomeDataPayload) => {
      this.emit('home:data', data);
    });

    // Daily bonus result
    socket.on(ServerEvent.DAILY_BONUS_RESULT, (data: DailyBonusResultPayload) => {
      this.emit('daily_bonus:result', data);
    });

    // Balance update
    socket.on(ServerEvent.BALANCE_UPDATE, (data: { stacks: number; gold: number; diceTokens?: number }) => {
      this.emit('balance:update', data);
    });

    // Offers (economy spec §8) — personalised live set + pipeline-change ping.
    socket.on(ServerEvent.OFFERS_ACTIVE, (data: { offers: OfferWire[] }) => {
      this.emit('offers:active', data);
    });
    socket.on(ServerEvent.OFFERS_UPDATE, () => {
      this.emit('offers:update', {});
    });
    socket.on(ServerEvent.OFFER_CLAIM_RESULT, (data: OfferClaimResult) => {
      this.emit('offer:claim_result', data);
    });

    // Connect-account bonus result
    socket.on(ServerEvent.CONNECT_BONUS_RESULT, (data: { success: boolean; amount: number; newBalance: number; error?: string }) => {
      this.emit('account:connect_bonus_result', data);
    });

    // Social sign-in needs a username (new identity)
    socket.on(ServerEvent.AUTH_SOCIAL_NEEDS_USERNAME, (data: { defaultName: string; takenName?: string }) => {
      this.emit('auth:social_needs_username', data);
    });

    // Live username-availability answer
    socket.on(ServerEvent.USERNAME_CHECK_RESULT, (data: { requestId: number; username: string; available: boolean }) => {
      this.emit('username:check_result', data);
    });

    // Outcome of a Settings username change (success carries the applied name
    // + new gold balance; failure carries a reason + the cost).
    socket.on(ServerEvent.USERNAME_CHANGE_RESULT, (data: UsernameChangeResultPayload) => {
      this.emit('username:change_result', data);
    });
  }

  /** Fire a live username-availability query (see DebouncedUsernameChecker). */
  checkUsername(requestId: number, username: string): void {
    this.mainSocket?.emit(ClientEvent.USERNAME_CHECK, { requestId, username });
  }

  /** Commit a username change from Settings (costs Gold; server re-validates). */
  changeUsername(username: string): void {
    this.mainSocket?.emit(ClientEvent.USERNAME_CHANGE, { username });
  }

  loginGuest(): void {
    if (!this.mainSocket) return;
    const token = localStorage.getItem('sdl_auth_token');
    this.mainSocket.emit(ClientEvent.AUTH_GUEST, { token });
  }

  /**
   * Sign in / link via Apple/Google. Stored so it fires on (re)connect, and
   * emitted immediately if already connected (e.g. the name-prompt resend).
   */
  loginSocial(payload: { provider: 'google' | 'apple'; token: string; tokenType?: 'access' | 'id'; username?: string; guestToken?: string }): void {
    this.pendingSocialAuth = payload;
    if (this.mainSocket?.connected) {
      this.mainSocket.emit(ClientEvent.AUTH_SOCIAL, payload);
    }
  }

  findMatch(payload: { gameType: 'ceelo' | 'craps'; stakes: 'low' | 'medium' | 'high' | 'high_roller'; maxPlayers?: number }): void {
    if (!this.mainSocket) return;
    this.mainSocket.emit(ClientEvent.FIND_GAME, payload);
  }

  /** Request a single-player Solo Grind room. On success the server emits
   *  MATCH_FOUND (same path as findMatch). */
  findSoloGame(): void {
    if (!this.mainSocket) return;
    this.mainSocket.emit(ClientEvent.FIND_SOLO_GAME);
  }

  /**
   * Connect to the game server using the stored match data.
   * Called by main.ts AFTER scene + controllers are ready (UDG pattern:
   * setup first, join room second, so state:full is processed immediately).
   */
  connectToGameServer(): void {
    if (!this.pendingMatch) {
      console.warn('[NetworkManager] connectToGameServer called but no pending match');
      return;
    }
    const match = this.pendingMatch;
    this.pendingMatch = null;
    this.connectGameServer(match);
  }

  /**
   * Periodic clock-sync against the game server. Each round trip is one ack-
   * style emit: client stamps its sendTime, server replies with `serverTime =
   * Date.now()`. Offset is `serverTime - (sendTime + RTT/2)` where RTT is the
   * client-measured round-trip duration.
   *
   * Frequency: once on connect, then every 30s. Clocks rarely drift fast
   * enough to matter sooner. We keep the lowest-RTT sample within the run to
   * suppress jitter from one-off network spikes.
   */
  private bestRttMs = Infinity;

  private startTimeSync(): void {
    this.stopTimeSync();
    this.bestRttMs = Infinity;
    const sync = () => {
      const socket = this.gameSocket;
      if (!socket || !socket.connected) return;
      const sentAt = Date.now();
      socket.timeout(5000).emit('time:sync', { clientTime: sentAt }, (err: Error | null, resp: { serverTime: number } | undefined) => {
        if (err || !resp || typeof resp.serverTime !== 'number') return;
        const receivedAt = Date.now();
        const rtt = receivedAt - sentAt;
        // Only adopt the sample if RTT is the lowest we've seen — minimises
        // the half-RTT estimation error.
        if (rtt > this.bestRttMs) return;
        this.bestRttMs = rtt;
        this.timeOffset = resp.serverTime + rtt / 2 - receivedAt;
      });
    };
    sync();
    this.timeSyncInterval = setInterval(sync, 30000);
  }

  private stopTimeSync(): void {
    if (this.timeSyncInterval) {
      clearInterval(this.timeSyncInterval);
      this.timeSyncInterval = null;
    }
  }

  private clearGameConnectDeadline(): void {
    if (this.gameConnectDeadline) {
      clearTimeout(this.gameConnectDeadline);
      this.gameConnectDeadline = null;
    }
  }

  private connectGameServer(match: MatchFoundPayload, reconnectionToken?: string): void {
    if (!this.playerId) return;

    const gameServerUrl = serverConfig.gameServerUrl ?? this.normalizeGameServerUrl(match.gameServerUrl);
    if (!gameServerUrl) return;

    if (this.gameSocket) {
      this.gameSocket.disconnect();
      this.gameSocket = null;
    }

    // Try to get saved reconnection token if not provided
    let token = reconnectionToken;
    if (!token) {
      try {
        const savedRoomId = localStorage.getItem('sdl_room_id');
        if (savedRoomId === match.roomId) {
          token = localStorage.getItem('sdl_reconnect_token') ?? undefined;
        }
      } catch (e) {
        console.warn('[NetworkManager] Failed to get reconnection token', e);
      }
    }

    this.gameSocket = io(gameServerUrl, {
      transports: import.meta.env.DEV ? ['polling', 'websocket'] : ['websocket'],
      query: {
        playerId: this.playerId,
        roomId: match.roomId,
        token: match.token,
        reconnectionToken: token,
        // Attaching a token means "I already have a seat in this exact room"
        // (the stored token is room-scoped above). Marking it lets the server
        // BOUNCE us (RESUME_GONE) when that seat was kicked or the room
        // closed, instead of silently fresh-joining with a stale balance.
        resume: token ? '1' : undefined,
      },
    });

    // Guard the INITIAL connect: if the allocated game server is unreachable
    // (pod down, bad URL, TLS failure) the socket would retry silently forever
    // and leave the player stranded in a half-built game. Give it a hard
    // deadline; if we never connect, stop retrying and surface the failure so
    // the UI can bounce the player back with a message.
    this.gameEverConnected = false;
    this.clearGameConnectDeadline();
    this.gameConnectDeadline = setTimeout(() => {
      if (this.gameEverConnected) return;
      console.error('[NetworkManager] Game server connection timed out');
      this.clearGameConnectDeadline();
      this.gameSocket?.disconnect();
      this.gameSocket = null;
      this.emit('game:connect_failed', undefined);
    }, 12000);
    this.gameSocket.on('connect_error', (err) => {
      console.warn('[NetworkManager] Game server connect_error:', err?.message ?? err);
    });

    // Server-side join/resume rejections arrive as a custom 'error' event
    // followed by a disconnect. RESUME_GONE = we tried to resume a seat that
    // was kicked at a round boundary (or the room closed) while we were
    // backgrounded. Clear the stale room keys so the next join is clean, and
    // reuse the kicked flow: main screen + "the table moved on" notice.
    // Every other join rejection (ROOM_FULL / ALREADY_IN_GAME /
    // INSUFFICIENT_FUNDS / JOIN_FAILED / INVALID_TOKEN) MUST also surface —
    // swallowing them left the player stuck on the loading screen.
    this.gameSocket.on('error', (data: { code?: string; message?: string }) => {
      if (!data?.code) return;
      if (data.code === 'RESUME_GONE') {
        try {
          localStorage.removeItem('sdl_reconnect_token');
          localStorage.removeItem('sdl_room_id');
        } catch { /* best effort */ }
        this.emit('game:kicked', { reason: 'resume_gone' });
        return;
      }
      this.emit('game:join_rejected', { code: data.code, message: data.message ?? '' });
    });

    // Save room ID for potential reconnection
    try {
      localStorage.setItem('sdl_room_id', match.roomId);
    } catch (e) {
      console.warn('[NetworkManager] Failed to save room ID', e);
    }

    this.gameSocket.on('connect', () => {
      this.gameEverConnected = true;
      this.clearGameConnectDeadline();
      this.emit('game:connected', undefined);
      this.startTimeSync();
    });

    this.gameSocket.on('disconnect', () => {
      this.clearGameConnectDeadline();
      this.emit('game:disconnected', undefined);
      this.stopTimeSync();
    });

    this.gameSocket.on(ServerEvent.GAME_STATE, (data: GameStateSnapshot) => {
      this.lastGameState = data;
      this.emit('game:state', data);
    });

    this.gameSocket.on(ServerEvent.GAME_PLAYER_JOINED, (data: { playerId: string; username: string; equippedDice?: string[] }) => {
      this.emit('game:player_joined', data);
    });

    this.gameSocket.on(ServerEvent.GAME_TURN_START, (data: GameTurnStartPayload) => {
      this.emit('game:turn_start', data);
    });

    // Roll authorization is STATE (game:update.rollAuthorizedFor), consumed by
    // the v2 roll states. The server's legacy GAME_ROLL_AUTHORIZED /
    // game:roll_cancelled / DICE_AUTHORIZE unicasts are intentionally NOT
    // relayed — they're transient and lost on background→resume; the
    // state-machine resume path (state:full) reconstructs roll setup instead.

    this.gameSocket.on(ServerEvent.GAME_ROLL_RESULT, (data: GameRollResultPayload) => {
      this.emit('game:roll_result', data);
    });

    this.gameSocket.on(ServerEvent.GAME_BET_PLACED, (data: GameBetPlacedPayload) => {
      this.emit('game:bet_placed', data);
    });

    this.gameSocket.on(ServerEvent.GAME_BET_ZONE_CREATE_ACK, (data: { requestId: string; zoneId: string }) => {
      this.emit('game:bet_zone_create_ack', data);
    });

    this.gameSocket.on(ServerEvent.GAME_BET_ZONE_FADE_RESULT, (data: { requestId: string; zoneId: string; acceptedAmount: number; rejectedAmount: number }) => {
      this.emit('game:bet_zone_fade_result', data);
    });

    this.gameSocket.on(ServerEvent.GAME_BET_ZONE_CREATE_REJECTED, (data: { requestId: string; reason: string; maxAmount?: number }) => {
      this.emit('game:bet_zone_create_rejected', data);
    });
    this.gameSocket.on(ServerEvent.GAME_DICE_SYNC, (data: DiceSyncPayload) => {
      this.emit('game:dice_sync', data);
    });

    this.gameSocket.on(ServerEvent.DICE_SHAKE, (data: { playerId: string; intensity?: number }) => {
      this.emit('dice:shake', data);
    });

    this.gameSocket.on(ServerEvent.GAME_CHAT_MESSAGE, (data: GameChatMessage) => {
      this.emit('game:chat_message', data);
    });

    // Server kicked us at a round boundary (we'd backgrounded / gone inactive).
    // The seat is already gone server-side, so don't send room:leave — just
    // surface it so the UI can show the modal and exit to the lobby. Clear
    // the room-scoped resume keys so a later reconnect doesn't try (and get
    // bounced) resuming the dead seat.
    this.gameSocket.on(ServerEvent.GAME_KICKED, (data: { reason?: string }) => {
      try {
        localStorage.removeItem('sdl_reconnect_token');
        localStorage.removeItem('sdl_room_id');
      } catch { /* best effort */ }
      this.emit('game:kicked', data ?? {});
    });

    // Voice chat signaling — relayed straight through to the VoiceClient.
    this.gameSocket.on(ServerEvent.VOICE_WELCOME, (data: VoiceWelcomePayload) => {
      this.emit('voice:welcome', data);
    });
    this.gameSocket.on(ServerEvent.VOICE_PEER_JOINED, (data: VoicePeerJoinedPayload) => {
      this.emit('voice:peer-joined', data);
    });
    this.gameSocket.on(ServerEvent.VOICE_PEER_LEFT, (data: VoicePeerLeftPayload) => {
      this.emit('voice:peer-left', data);
    });
    this.gameSocket.on(ServerEvent.VOICE_OFFER, (data: VoiceSdpRelayPayload) => {
      this.emit('voice:offer', data);
    });
    this.gameSocket.on(ServerEvent.VOICE_ANSWER, (data: VoiceSdpRelayPayload) => {
      this.emit('voice:answer', data);
    });
    this.gameSocket.on(ServerEvent.VOICE_ICE, (data: VoiceIceRelayPayload) => {
      this.emit('voice:ice', data);
    });
    this.gameSocket.on(ServerEvent.VOICE_END, (data: VoiceEndRelayPayload) => {
      this.emit('voice:end', data);
    });

    // Server-authoritative events
    this.gameSocket.on(ServerEvent.STATE_FULL, (data: FullGameState) => {
      this.emit('state:full', data);
    });

    this.gameSocket.on(ServerEvent.DICE_PHASE, (data: {
      playerId: string;
      phase: DicePhase;
      throwParams?: DiceThrowParams;
      finalStates?: Array<{
        position: { x: number; y: number; z: number };
        quaternion: { x: number; y: number; z: number; w: number };
      }>;
    }) => {
      this.emit('dice:phase', data);
    });

    this.gameSocket.on(ServerEvent.DICE_RESULT, (data: { playerId: string; results: number[] }) => {
      this.emit('dice:result', data);
    });

    this.gameSocket.on(ServerEvent.RECONNECT_TOKEN, (data: { token: string }) => {
      this.emit('reconnect:token', data);
    });

    this.gameSocket.on(ServerEvent.RECONNECT_OK, (data: { playerId: string }) => {
      this.emit('reconnect:ok', data);
    });

    this.gameSocket.on(ServerEvent.RECONNECT_FAIL, (data: { code: string; message: string }) => {
      this.emit('reconnect:fail', data);
    });

    // Config sync from server (when editor updates shooting params)
    this.gameSocket.on('config:shooting', (data: ShootingConfigPayload) => {
      this.emit('config:shooting', data);
    });

    // Payout events (bet wins, side bet wins)
    this.gameSocket.on(ServerEvent.GAME_PAYOUT, (data: { amount: number; newBalance: number; reason?: string }) => {
      this.emit('game:payout', data);
    });

    // Roll wins broadcast
    this.gameSocket.on(ServerEvent.GAME_ROLL_WINS, (data: { wins: Array<{ playerId: string; amount: number; betType: string }> }) => {
      this.emit('game:roll_wins', data);
    });

    // Unified game update
    this.gameSocket.on(ServerEvent.GAME_UPDATE, (data: GameUpdate) => {
      this.emit('game:update', data);
    });

    // Craps: one-roll sidebet resolved against the just-completed dice
    // result. Forwarded as a typed event so GameFlowController can play
    // the per-zone payout flyout.
    this.gameSocket.on('craps:sidebet_resolved', (data: NetworkEvents['craps:sidebet_resolved']) => {
      this.emit('craps:sidebet_resolved', data);
    });

    // Craps: end-of-turn / forfeit beat broadcast.
    this.gameSocket.on('craps:turn_result', (data: NetworkEvents['craps:turn_result']) => {
      this.emit('craps:turn_result', data);
    });

    // Sidebet window broadcast
    this.gameSocket.on(ServerEvent.GAME_SIDEBET_WINDOW, (data: { requestedBy: string }) => {
      this.emit('game:sidebet_window', data);
    });
  }

  private normalizeGameServerUrl(url: string): string {
    if (url.startsWith('ws://')) {
      return url.replace('ws://', 'http://');
    }

    if (url.startsWith('wss://')) {
      return url.replace('wss://', 'https://');
    }

    return url;
  }

  getPlayerId(): string | null {
    return this.playerId;
  }

  getLastGameState(): GameStateSnapshot | null {
    return this.lastGameState;
  }

  requestHomeData(): void {
    if (!this.mainSocket) return;
    this.mainSocket.emit(ClientEvent.HOME_DATA_REQUEST, {});
  }

  claimDailyBonus(): void {
    if (!this.mainSocket) return;
    this.mainSocket.emit(ClientEvent.DAILY_BONUS_CLAIM, {});
  }

  /** Report a completed tutorial module — server grants its reward idempotently. */
  completeTutorialModule(module: 'shoot' | 'bet'): void {
    if (!this.mainSocket) return;
    this.mainSocket.emit(ClientEvent.TUTORIAL_MODULE_COMPLETE, { module });
  }

  /** Report a completed tutorial-part-2 task — server flips it idempotently and
   *  grants the page die when a page's 4 tasks are done. */
  sendTutorial2Task(task: Tutorial2TaskId): void {
    this.mainSocket?.emit(ClientEvent.TUTORIAL2_TASK_COMPLETE, { task });
  }

  /** Collect a daily challenge's completed levels — the server re-validates
   *  completion + claim state atomically and credits the gold. */
  sendDailyChallengeClaim(challengeId: DailyChallengeId): void {
    this.mainSocket?.emit(ClientEvent.DAILY_CHALLENGE_CLAIM, { challengeId });
  }

  /** Report that the player opened the rules manual (analytics only). */
  reportManualOpened(section: string): void {
    this.mainSocket?.emit(ClientEvent.MANUAL_OPENED, { section });
  }

  /** Skip the tutorial — server grants the dice (money is the connect bonus). */
  skipTutorial(): void {
    if (!this.mainSocket) return;
    this.mainSocket.emit(ClientEvent.TUTORIAL_SKIP, {});
  }

  /** Claim the 100M connect-account bonus (server gates on linked + unclaimed). */
  claimConnectBonus(): void {
    if (!this.mainSocket) return;
    this.mainSocket.emit(ClientEvent.ACCOUNT_CLAIM_CONNECT_BONUS, {});
  }

  sendReady(): void {
    if (!this.gameSocket) return;
    this.gameSocket.emit(ClientEvent.GAME_READY, {});
  }

  sendUnready(): void {
    if (!this.gameSocket) return;
    this.gameSocket.emit(ClientEvent.GAME_UNREADY, {});
  }

  /** True while connected to a game-server room. */
  isInGame(): boolean {
    return !!this.gameSocket && this.gameSocket.connected;
  }

  /**
   * Deliberate leave (menu exit / app unload) — clean-remove from the game
   * room (settle + drop), as opposed to a transient socket disconnect (which
   * the server keeps as an inactive seat for reconnect). Best-effort on
   * `beforeunload`: the emit may not flush before the socket closes, in which
   * case the server's disconnect→inactive path is the fallback.
   */
  leaveRoom(): void {
    const socket = this.gameSocket;
    if (!socket) return;
    // Deliberate leave. Detach immediately so nothing else uses the socket,
    // then tear it down so socket.io's auto-reconnect doesn't keep retrying the
    // now-gone room (the 503 / CORS reconnect spam after leaving). We KILL
    // reconnection before the server closes us, send the clean-leave signal, and
    // close after a short tick so the LEAVE_ROOM packet flushes first. (Auto-
    // reconnect stays enabled for genuine drops — that path never calls this.)
    this.gameSocket = null;
    this.stopTimeSync();
    this.clearGameConnectDeadline();
    socket.io.reconnection(false);
    socket.removeAllListeners();
    // The server ACKs the leave — disconnect the moment it lands. The timer
    // stays as the flush fallback for a dead/laggy link (beforeunload path).
    let closed = false;
    const close = () => {
      if (closed) return;
      closed = true;
      socket.disconnect();
    };
    socket.emit(ClientEvent.LEAVE_ROOM, {}, close);
    setTimeout(close, 250);
    try {
      localStorage.removeItem('sdl_room_id');
      localStorage.removeItem('sdl_reconnect_token');
    } catch (e) {
      // Removal can fail (private mode / quota). Blank the values instead — a
      // stale reconnect token must not pull a later session back into a room
      // this player deliberately left.
      try {
        localStorage.setItem('sdl_room_id', '');
        localStorage.setItem('sdl_reconnect_token', '');
      } catch { /* storage fully unavailable — nothing persisted to go stale */ }
      console.warn('[NetworkManager] Failed to clear room reconnect keys', e);
    }
  }

  /** Request a re-buy — tops the table stack from the bank (server-bounded). */
  sendRebuy(amount: number): void {
    if (!this.gameSocket) return;
    this.gameSocket.emit(ClientEvent.GAME_REBUY, { amount });
  }

  sendBet(amount: number, denominations?: number[]): void {
    if (!this.gameSocket) return;
    this.gameSocket.emit(ClientEvent.GAME_BET, { amount, denominations });
  }

  /** Send an in-match public chat line. Server validates + relays to the room. */
  sendGameChat(text: string): void {
    if (!this.gameSocket) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    this.gameSocket.emit(ClientEvent.GAME_CHAT_SEND, { text: trimmed });
  }

  // -- Voice chat signaling (WebRTC). The server derives the voice room from
  //    the seated game socket, so no matchId/roomId is sent. ----------------

  /** Opt into the match voice mesh. Server replies with `voice:welcome`. */
  sendVoiceJoin(): void {
    if (!this.gameSocket) return;
    this.gameSocket.emit(ClientEvent.VOICE_JOIN, {});
  }

  /** Opt out of the voice mesh (settings toggle off / leaving the table). */
  sendVoiceLeave(): void {
    if (!this.gameSocket) return;
    this.gameSocket.emit(ClientEvent.VOICE_LEAVE, {});
  }

  sendVoiceOffer(to: string, sdp: RTCSessionDescriptionInit): void {
    if (!this.gameSocket) return;
    this.gameSocket.emit(ClientEvent.VOICE_OFFER, { to, sdp });
  }

  sendVoiceAnswer(to: string, sdp: RTCSessionDescriptionInit): void {
    if (!this.gameSocket) return;
    this.gameSocket.emit(ClientEvent.VOICE_ANSWER, { to, sdp });
  }

  sendVoiceIce(to: string, candidate: RTCIceCandidateInit): void {
    if (!this.gameSocket) return;
    this.gameSocket.emit(ClientEvent.VOICE_ICE, { to, candidate });
  }

  sendVoiceEnd(to: string): void {
    if (!this.gameSocket) return;
    this.gameSocket.emit(ClientEvent.VOICE_END, { to });
  }

  sendBetZoneCreate(requestId: string, amount: number, betType: string, targetPlayerId?: string, denominations?: number[]): void {
    if (!this.gameSocket) return;
    this.gameSocket.emit(ClientEvent.GAME_BET_ZONE_CREATE, { requestId, amount, betType, targetPlayerId, denominations });
  }

  sendBetZoneFade(requestId: string, zoneId: string, amount: number, denominations?: number[]): void {
    if (!this.gameSocket) return;
    this.gameSocket.emit(ClientEvent.GAME_BET_ZONE_FADE, { requestId, zoneId, amount, denominations });
  }

  /** Owner cancels their own placed, still-unfaded sidebet zone. No ack —
   *  acceptance rides game:update (zone removed + balance refunded); a fade
   *  that won the race leaves the zone in place with fadedAmount > 0. */
  sendBetZoneCancel(zoneId: string): void {
    if (!this.gameSocket) return;
    this.gameSocket.emit(ClientEvent.GAME_BET_ZONE_CANCEL, { zoneId });
  }

  /** Solo grind: pick the sub-game (start-screen selection + pre-bet toggle). */
  sendSoloMode(mode: 'hilo' | 'chuckaluck'): void {
    if (!this.gameSocket) return;
    this.gameSocket.emit('solo:set_mode', { mode });
  }

  sendSideBet(amount: number, type?: string, targetPlayerId?: string): void {
    if (!this.gameSocket) return;
    this.gameSocket.emit(ClientEvent.GAME_SIDE_BET, { amount, type, targetPlayerId });
  }

  sendRequestSidebetWindow(): void {
    if (!this.gameSocket) return;
    this.gameSocket.emit(ClientEvent.GAME_REQUEST_SIDEBET_WINDOW);
  }

  sendPlayerReady(version: number): void {
    if (!this.gameSocket) return;
    this.gameSocket.emit(ClientEvent.PLAYER_READY_ACK, { version });
  }

  /** Notify server that this player's pending bet crossed 0 ↔ non-zero. */
  sendBettingActivity(active: boolean): void {
    if (!this.gameSocket) return;
    this.gameSocket.emit(ClientEvent.BETTING_ACTIVITY, { active });
  }

  /** Craps: toggle pre-arm for the sidebet window. Server augments the
   *  payload with the authenticated playerId before dispatching. */
  sendSidebetButtonPress(): void {
    if (!this.gameSocket) return;
    this.gameSocket.emit(ClientEvent.SIDEBET_BUTTON_PRESS, {});
  }

  /** Live equipped-dice swap from the in-game dice library. No-op outside a
   *  game (no gameSocket) — safe to call from the lobby equip path too. The
   *  server replaces the sender's seat equippedDice and rebroadcasts. */
  sendChangeEquippedDice(slots: (string | null)[]): void {
    if (!this.gameSocket) return;
    this.gameSocket.emit(ClientEvent.GAME_CHANGE_EQUIPPED_DICE, { slots });
  }

  sendRollResult(dice: number[]): void {
    if (!this.gameSocket) return;
    this.gameSocket.emit(ClientEvent.GAME_ROLL_RESULT, { dice });
  }

  // Server-authoritative dice phase methods

  sendDicePickupStart(): void {
    if (!this.gameSocket) return;
    this.gameSocket.emit(ClientEvent.DICE_PICKUP_START, {
      timestamp: Date.now(),
    });
  }

  sendDiceCollected(): void {
    if (!this.gameSocket) return;
    this.gameSocket.emit(ClientEvent.DICE_COLLECTED, {
      timestamp: Date.now(),
    });
  }

  sendDiceThrowRequest(
    diceStates: Array<{
      position: { x: number; y: number; z: number };
      rotation: { x: number; y: number; z: number; w: number };
      velocity: { x: number; y: number; z: number };
      angularVelocity: { x: number; y: number; z: number };
    }>,
    halfExtents?: [number, number, number],
    halfExtentsPerDie?: Array<[number, number, number]>,
  ): void {
    if (!this.gameSocket) return;
    this.gameSocket.emit(ClientEvent.DICE_THROW, {
      diceStates,
      halfExtents,
      halfExtentsPerDie,
      timestamp: Date.now(),
    });
  }

  sendDiceDrop(
    diceStates: Array<{
      position: { x: number; y: number; z: number };
      rotation: { x: number; y: number; z: number; w: number };
      velocity: { x: number; y: number; z: number };
      angularVelocity: { x: number; y: number; z: number };
    }>,
    halfExtents?: [number, number, number],
    halfExtentsPerDie?: Array<[number, number, number]>,
  ): void {
    if (!this.gameSocket) return;
    this.gameSocket.emit(ClientEvent.DICE_DROP, {
      diceStates,
      halfExtents,
      halfExtentsPerDie,
      timestamp: Date.now(),
    });
  }

  sendDiceStoppedRequest(
    results: number[],
    finalStates?: Array<{
      position: { x: number; y: number; z: number };
      quaternion: { x: number; y: number; z: number; w: number };
    }>,
  ): void {
    if (!this.gameSocket) return;
    this.gameSocket.emit(ClientEvent.DICE_STOPPED, {
      results,
      finalStates,
      timestamp: Date.now(),
    });
  }

  sendDiceShake(intensity: number): void {
    if (!this.gameSocket) return;
    this.gameSocket.emit(ClientEvent.DICE_SHAKE, { timestamp: Date.now(), intensity });
  }

  sendDiceDragSync(position: { x: number; y: number; z: number }, rotation: { x: number; y: number; z: number; w: number }, seq: number, timestamp: number): void {
    if (!this.gameSocket) return;
    this.gameSocket.emit(ClientEvent.DICE_DRAG, {
      position,
      rotation,
      timestamp,
      seq,
    });
  }

  sendStateRequest(reconnectionToken?: string): void {
    if (!this.gameSocket) return;
    this.gameSocket.emit(ClientEvent.STATE_REQUEST, {
      reconnectionToken,
    });
  }

  /**
   * Ensure the game-server socket is actually connected before we emit on it.
   * After a long background the socket object survives but its connection is
   * dead, so emitting `state:request` / active-status into it silently goes
   * nowhere and resume hangs. On resume we call this first: it forces a
   * reconnect (re-running the join query, incl. the reconnection token) and
   * resolves once connected, or false on timeout / no socket.
   */
  ensureGameConnected(timeoutMs = 6000): Promise<boolean> {
    const sock = this.gameSocket;
    if (!sock) return Promise.resolve(false);
    if (sock.connected) return Promise.resolve(true);
    // The handshake query was frozen when the socket was created — often before
    // the server had issued our reconnection token, so a first-session resume
    // would reconnect with reconnectionToken:undefined and the server would
    // spawn a duplicate seat. Refresh it from the stored token so the server
    // re-keys our existing seat. (Server-side dedupe covers us if this is blocked.)
    try {
      const stored = localStorage.getItem('sdl_reconnect_token');
      const mgrOpts = sock.io?.opts as { query?: Record<string, unknown> } | undefined;
      if (stored && mgrOpts) {
        // resume:'1' lets the server bounce us (RESUME_GONE → main screen +
        // notice) when the seat was kicked / room closed while backgrounded,
        // instead of silently re-seating us with a stale-balance token.
        mgrOpts.query = { ...(mgrOpts.query ?? {}), reconnectionToken: stored, resume: '1' };
      }
    } catch { /* localStorage blocked — best effort */ }
    return new Promise<boolean>((resolve) => {
      let done = false;
      const finish = (ok: boolean) => {
        if (done) return;
        done = true;
        sock.off('connect', onConnect);
        clearTimeout(timer);
        resolve(ok);
      };
      const onConnect = () => finish(true);
      sock.on('connect', onConnect);
      const timer = setTimeout(() => finish(false), timeoutMs);
      sock.connect(); // no-op if a reconnect is already in flight
    });
  }

  /**
   * Inform the server whether this client is currently foregrounded. Server
   * treats !active as "locked in" in its all-players-ready checks so the
   * round doesn't stall on a backgrounded player.
   */
  sendActiveStatus(active: boolean): void {
    if (!this.gameSocket) return;
    // `ts` lets the server reject stale/out-of-order flips. On flaky wifi the
    // background/foreground events can arrive out of order; without this the
    // server's last-write-wins-by-arrival leaves a player stuck in the wrong
    // active state, which corrupts readiness/roll gating for the whole room.
    // Date.now() (not a counter) so it stays monotonic across reload/reconnect.
    this.gameSocket.emit(ClientEvent.PLAYER_ACTIVE_STATUS, { active, ts: Date.now() });
  }

  /**
   * Tell main-server the app just returned from background/hidden state.
   * Server may register a late "daily login" if lastLogin > 8h old (covers
   * the edge case where a long-lived socket stayed connected through a
   * multi-day idle and the AUTH-on-reconnect path never fires).
   */
  sendClientResume(): void {
    if (!this.mainSocket) return;
    this.mainSocket.emit(ClientEvent.CLIENT_RESUME);
  }

  /** Ask the server for a fresh personalised offer set (foreground / resume). */
  requestOffers(): void {
    this.mainSocket?.emit(ClientEvent.OFFERS_REQUEST);
  }

  /** Claim a free / video offer. Result arrives via 'offer:claim_result'. */
  claimOffer(offerId: string, payload?: { adsWatched?: number }): void {
    this.mainSocket?.emit(ClientEvent.OFFER_CLAIM, { offerId, ...payload });
  }

  // Dice gear methods

  sendEquipDice(preset: number, slots: (string | null)[]): void {
    if (!this.mainSocket) return;
    this.mainSocket.emit(ClientEvent.PLAYER_EQUIP_DICE, { preset, slots });
  }

  sendSwitchPreset(preset: number): void {
    if (!this.mainSocket) return;
    this.mainSocket.emit(ClientEvent.PLAYER_SWITCH_PRESET, { preset });
  }

  // Social methods

  sendChatMessage(to: string, content: string, type: ChatMessageType = 'text', imageUrl?: string, inviteData?: { gameType: 'ceelo' | 'craps'; stakes: string; roomId?: string }): void {
    if (!this.mainSocket) return;
    this.mainSocket.emit(ClientEvent.CHAT_SEND, { to, content, type, imageUrl, inviteData });
  }

  /** Ack that a received chat photo is stored locally — lets the server
   *  delete the temporary CDN copy. */
  sendChatImageStored(url: string): void {
    if (!this.mainSocket) return;
    this.mainSocket.emit(ClientEvent.CHAT_IMAGE_STORED, { url });
  }

  sendFriendAdd(playerId: string): void {
    if (!this.mainSocket) return;
    this.mainSocket.emit(ClientEvent.FRIEND_ADD, { playerId });
  }

  sendFriendRemove(playerId: string): void {
    if (!this.mainSocket) return;
    this.mainSocket.emit(ClientEvent.FRIEND_REMOVE, { playerId });
  }

  /** Withdraw an outgoing friend request that hasn't been answered yet. */
  sendFriendRequestCancel(playerId: string): void {
    if (!this.mainSocket) return;
    this.mainSocket.emit(ClientEvent.FRIEND_REQUEST_CANCEL, { playerId });
  }

  sendFriendFavourite(playerId: string, isFavourite: boolean): void {
    if (!this.mainSocket) return;
    this.mainSocket.emit(ClientEvent.FRIEND_FAVOURITE, { playerId, isFavourite });
  }

  /** Send today's free 10 Gold gift to a friend. Server confirms via friend_gift:sent. */
  sendFriendGift(playerId: string): void {
    if (!this.mainSocket) return;
    this.mainSocket.emit(ClientEvent.FRIEND_GIFT_SEND, { playerId });
  }

  sendFriendUnblock(playerId: string): void {
    if (!this.mainSocket) return;
    this.mainSocket.emit(ClientEvent.FRIEND_UNBLOCK, { playerId });
  }

  sendFriendBlock(playerId: string): void {
    if (!this.mainSocket) return;
    this.mainSocket.emit(ClientEvent.FRIEND_BLOCK, { playerId });
  }

  searchPlayers(query: string): void {
    if (!this.mainSocket) return;
    this.mainSocket.emit(ClientEvent.PLAYER_SEARCH, { query });
  }

  // Push notifications — register/unregister this install's FCM device token.

  registerPushToken(token: string, platform: 'ios' | 'android' | 'web'): void {
    if (!this.mainSocket || !token) return;
    this.mainSocket.emit(ClientEvent.PUSH_REGISTER_TOKEN, { token, platform });
  }

  unregisterPushToken(token: string): void {
    if (!this.mainSocket || !token) return;
    this.mainSocket.emit(ClientEvent.PUSH_UNREGISTER_TOKEN, { token });
  }

  // Lobby methods

  /** Subscribe to the public lobby channel. Server replies with lobby:state.
   *  Pass `{ solo: true }` from solo grind so the server sends room broadcasts
   *  but keeps the player out of public presence (no lobby profile card). */
  subscribeLobby(opts?: { solo?: boolean }): void {
    if (!this.mainSocket) return;
    this.mainSocket.emit(ClientEvent.LOBBY_SUBSCRIBE, { solo: opts?.solo ?? false });
  }

  /** Leave the lobby channel. Idempotent — safe to call when not subscribed. */
  unsubscribeLobby(): void {
    if (!this.mainSocket) return;
    this.mainSocket.emit(ClientEvent.LOBBY_UNSUBSCRIBE);
  }

  /** Send a public chat message to the lobby. Server rate-limits + broadcasts. */
  sendLobbyChat(text: string): void {
    if (!this.mainSocket) return;
    this.mainSocket.emit(ClientEvent.LOBBY_CHAT_SEND, { text });
  }

  /** Request creation of a new room. On success the server emits MATCH_FOUND. */
  createLobbyRoom(payload: LobbyCreateRoomPayload): void {
    if (!this.mainSocket) return;
    this.mainSocket.emit(ClientEvent.LOBBY_CREATE_ROOM, payload);
  }

  /** Request joining an existing room. On success the server emits MATCH_FOUND. */
  joinLobbyRoom(payload: LobbyJoinRoomPayload): void {
    if (!this.mainSocket) return;
    this.mainSocket.emit(ClientEvent.LOBBY_JOIN_ROOM, payload);
  }

  // Notification methods

  sendFriendRequestAccept(notificationId: string): void {
    if (!this.mainSocket) return;
    this.mainSocket.emit(ClientEvent.FRIEND_REQUEST_ACCEPT, { notificationId });
  }

  sendFriendRequestDecline(notificationId: string): void {
    if (!this.mainSocket) return;
    this.mainSocket.emit(ClientEvent.FRIEND_REQUEST_DECLINE, { notificationId });
  }

  sendNotificationsSeen(): void {
    if (!this.mainSocket) return;
    this.mainSocket.emit(ClientEvent.NOTIFICATIONS_SEEN);
  }

  dismissNotification(notificationId: string): void {
    this.mainSocket?.emit(ClientEvent.NOTIFICATION_DISMISS, { notificationId });
  }

  dismissAllNotifications(): void {
    this.mainSocket?.emit(ClientEvent.NOTIFICATION_DISMISS_ALL);
  }

  /** Collect one gold gift from the inbox. Server replies with gift:collected. */
  collectGift(notificationId: string): void {
    this.mainSocket?.emit(ClientEvent.GIFT_COLLECT, { notificationId });
  }

  /** Collect every pending gold gift at once (the Collect all button). */
  collectAllGifts(): void {
    this.mainSocket?.emit(ClientEvent.GIFT_COLLECT_ALL);
  }
}
